import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { mockCargoCategories } from '../mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RequestQuote = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [freightImage, setFreightImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [checkingAccount, setCheckingAccount] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null); // null, 'exists', 'new'
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
    phone: '',
    company_name: '',
    abn: '',
    address: ''
  });
  const [formData, setFormData] = useState({
    shipper_email: '',
    contact_phone: '',
    company_name: '',
    pickup_suburb: '',
    pickup_state: '',
    dropoff_suburb: '',
    dropoff_state: '',
    cargo_type: '',
    cargo_description: '',
    cargo_value: '',
    weight: '',
    weight_unit: 'kg',
    dimensions: '',
    dimension_length: '',
    dimension_width: '',
    dimension_height: '',
    dimension_unit: 'm',
    oversize_ground_contact_dimensions: '',
    oversize_extremity_dimensions: '',
    pickup_date: '',
    delivery_date: '',
    pickup_time_window: '',
    delivery_time_window: '',
    insurance_requirements: '',
    loading_equipment_needed: '',
    unloading_equipment_needed: '',
    additional_contact_person: '',
    additional_contact_phone: '',
    special_requirements: '',
    pallets: []  // [{ weight, weight_unit, length, width, height, dimension_unit }]
  });

  // Check if user is already logged in
  useEffect(() => {
    const userEmail = localStorage.getItem('user_email');
    if (userEmail) {
      setFormData(prev => ({ ...prev, shipper_email: userEmail }));
      setAccountStatus('exists');
    }
  }, []);

  useEffect(() => {
    const savedRequest = localStorage.getItem('quoteRequest');
    if (savedRequest) {
      const data = JSON.parse(savedRequest);
      const parseLocation = (loc) => {
        if (!loc) return { suburb: '', state: '' };
        const parts = loc.split(',').map(s => s.trim());
        const states = ['NSW','VIC','QLD','WA','SA','TAS','NT','ACT'];
        if (parts.length >= 2 && states.includes(parts[parts.length - 1].toUpperCase())) {
          return { suburb: parts.slice(0, -1).join(', '), state: parts[parts.length - 1].toUpperCase() };
        }
        return { suburb: loc, state: '' };
      };
      const pickup = parseLocation(data.pickup);
      const dropoff = parseLocation(data.dropoff);
      setFormData(prev => ({
        ...prev,
        pickup_suburb: pickup.suburb,
        pickup_state: pickup.state,
        dropoff_suburb: dropoff.suburb,
        dropoff_state: dropoff.state
      }));
      localStorage.removeItem('quoteRequest');
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRegistrationChange = (e) => {
    const { name, value } = e.target;
    setRegistrationData(prev => ({ ...prev, [name]: value }));
  };

  // Check if email has an existing account
  const checkEmailAccount = async () => {
    if (!formData.shipper_email || !formData.shipper_email.includes('@')) {
      return;
    }
    
    setCheckingAccount(true);
    try {
      const response = await axios.get(`${API}/users/check-email?email=${encodeURIComponent(formData.shipper_email)}`);
      if (response.data.exists) {
        setAccountStatus('exists');
        setShowRegistration(false);
        // Pre-fill contact info if available
        if (response.data.phone) {
          setFormData(prev => ({ ...prev, contact_phone: response.data.phone }));
        }
        if (response.data.company_name) {
          setFormData(prev => ({ ...prev, company_name: response.data.company_name }));
        }
        toast.success('Account found! You can proceed to post your load.');
      } else {
        setAccountStatus('new');
        setShowRegistration(true);
        toast.info('No account found. Please create one to post your load.');
      }
    } catch (error) {
      // If endpoint doesn't exist or error, assume new account needed
      setAccountStatus('new');
      setShowRegistration(true);
    } finally {
      setCheckingAccount(false);
    }
  };

  // Create new shipper account
  const createShipperAccount = async () => {
    if (!registrationData.name || !registrationData.password) {
      toast.error('Please fill in your name and password');
      return false;
    }
    
    if (registrationData.password !== registrationData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    
    if (registrationData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    
    try {
      const userData = {
        email: formData.shipper_email,
        name: registrationData.name,
        password: registrationData.password,
        phone: registrationData.phone || formData.contact_phone,
        company_name: registrationData.company_name || formData.company_name,
        abn: registrationData.abn,
        address: registrationData.address,
        role: 'shipper'
      };
      
      await axios.post(`${API}/users/signup`, userData);
      
      // Auto-login after registration
      localStorage.setItem('user_email', formData.shipper_email);
      localStorage.setItem('user_roles', JSON.stringify(['shipper']));
      localStorage.setItem('active_role', 'shipper');
      
      setAccountStatus('exists');
      setShowRegistration(false);
      toast.success('Account created successfully!');
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create account';
      toast.error(errorMsg);
      return false;
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ---- Pallet helpers (for Palletised Freight) ----
  const newPallet = () => ({
    weight: '', weight_unit: 'kg',
    length: '', width: '', height: '', dimension_unit: 'm'
  });

  const handlePalletCountChange = (e) => {
    const raw = e.target.value;
    if (raw === '') { setFormData(prev => ({ ...prev, pallets: [] })); return; }
    let count = parseInt(raw, 10);
    if (isNaN(count) || count < 0) count = 0;
    if (count > 50) count = 50; // hard cap
    setFormData(prev => {
      const current = prev.pallets || [];
      let next;
      if (count > current.length) {
        next = [...current, ...Array.from({ length: count - current.length }, () => newPallet())];
      } else {
        next = current.slice(0, count);
      }
      return { ...prev, pallets: next };
    });
  };

  const handlePalletFieldChange = (idx, field, value) => {
    setFormData(prev => {
      const next = [...(prev.pallets || [])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, pallets: next };
    });
  };
  // ---- end Pallet helpers ----

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be smaller than 5MB');
        return;
      }
      
      setFreightImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      toast.success('Image selected successfully');
    }
  };

  const removeImage = () => {
    setFreightImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.shipper_email || !formData.contact_phone || !formData.pickup_suburb || !formData.pickup_state || !formData.dropoff_suburb || !formData.dropoff_state || !formData.cargo_type) {
      toast.error('Please fill in all required fields including suburb and state for pickup and dropoff');
      return;
    }

    // Validate pallets if Palletised Freight chosen
    if (formData.cargo_type === 'Palletised Freight') {
      if (!formData.pallets || formData.pallets.length === 0) {
        toast.error('Please specify the number of pallets and fill in each pallet\'s weight and dimensions');
        return;
      }
      const bad = formData.pallets.findIndex(p =>
        !p.weight || !p.length || !p.width || !p.height
      );
      if (bad !== -1) {
        toast.error(`Pallet #${bad + 1} is missing weight or dimensions`);
        return;
      }
    }

    // Check if account verification is needed
    if (accountStatus === null) {
      toast.error('Please verify your email address first');
      return;
    }

    // If new account, create it first
    if (accountStatus === 'new' && showRegistration) {
      const accountCreated = await createShipperAccount();
      if (!accountCreated) {
        return;
      }
    }

    setLoading(true);
    try {
      // Prepare submission data
      const submissionData = { ...formData };
      
      // Combine suburb + state into location fields
      submissionData.pickup_location = `${submissionData.pickup_suburb}, ${submissionData.pickup_state}`;
      submissionData.dropoff_location = `${submissionData.dropoff_suburb}, ${submissionData.dropoff_state}`;
      delete submissionData.pickup_suburb;
      delete submissionData.pickup_state;
      delete submissionData.dropoff_suburb;
      delete submissionData.dropoff_state;
      
      // Convert empty strings to null for numeric fields (Pydantic validation)
      if (submissionData.cargo_value === '') submissionData.cargo_value = null;

      // For Palletised Freight: auto-derive total weight & dimensions summary
      // from per-pallet data so existing carrier/admin views still show useful info.
      if (submissionData.cargo_type === 'Palletised Freight' && Array.isArray(submissionData.pallets) && submissionData.pallets.length > 0) {
        const palletsKg = submissionData.pallets.map(p => {
          const w = parseFloat(p.weight) || 0;
          return p.weight_unit === 't' ? w * 1000 : w;
        });
        const totalKg = palletsKg.reduce((a, b) => a + b, 0);
        // Use tonnes if >= 1000 kg
        if (totalKg >= 1000) {
          submissionData.weight = `${(totalKg / 1000).toFixed(2)} t`;
        } else {
          submissionData.weight = `${totalKg} kg`;
        }
        // Build a multi-line dimensions summary
        const dimSummary = submissionData.pallets
          .map((p, i) => `Pallet ${i + 1}: ${p.length}x${p.width}x${p.height} ${p.dimension_unit} (${p.weight} ${p.weight_unit})`)
          .join('; ');
        submissionData.dimensions = `${submissionData.pallets.length} pallets — ${dimSummary}`;
      } else if (submissionData.weight === '') {
        submissionData.weight = null;
      } else if (submissionData.weight) {
        // Format weight with unit (e.g., "1000 kg" or "2 t")
        submissionData.weight = `${submissionData.weight} ${submissionData.weight_unit}`;
      }
      
      // Format dimensions from separate fields (skip for Palletised Freight — already set above)
      if (submissionData.cargo_type !== 'Palletised Freight') {
        if (submissionData.dimension_length || submissionData.dimension_width || submissionData.dimension_height) {
          const parts = [
            submissionData.dimension_length ? `L: ${submissionData.dimension_length}` : '',
            submissionData.dimension_width ? `W: ${submissionData.dimension_width}` : '',
            submissionData.dimension_height ? `H: ${submissionData.dimension_height}` : ''
          ].filter(Boolean).join(' x ');
          submissionData.dimensions = `${parts} ${submissionData.dimension_unit}`;
        } else if (submissionData.dimensions) {
          submissionData.dimensions = `${submissionData.dimensions} ${submissionData.dimension_unit}`;
        }
      }
      
      // Remove unit fields from submission (they're now incorporated)
      delete submissionData.weight_unit;
      delete submissionData.dimension_unit;
      delete submissionData.dimension_length;
      delete submissionData.dimension_width;
      delete submissionData.dimension_height;
      
      // Add freight image if uploaded
      if (freightImage && imagePreview) {
        submissionData.freight_image = imagePreview; // base64 encoded image
      }
      
      const response = await axios.post(`${API}/loads`, submissionData);
      
      alert('Your load has been posted successfully!');
      navigate(`/load/${response.data.id}`);
    } catch (error) {
      alert('Failed to submit request. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-6">Request a Quote</h1>
          
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Contact Information */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">Contact Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="shipper_email">Email *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="shipper_email"
                      name="shipper_email"
                      type="email"
                      value={formData.shipper_email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      disabled={accountStatus === 'exists'}
                      className="flex-1"
                    />
                    {accountStatus !== 'exists' && (
                      <Button
                        type="button"
                        onClick={checkEmailAccount}
                        disabled={checkingAccount || !formData.shipper_email}
                        className="bg-[#1e3a5f] hover:bg-[#2d4a6f]"
                      >
                        {checkingAccount ? 'Checking...' : 'Verify Email'}
                      </Button>
                    )}
                  </div>
                  {accountStatus === 'exists' && (
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm text-green-600">✓ Account verified</p>
                      <button
                        type="button"
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                        onClick={() => {
                          setAccountStatus(null);
                          setFormData(prev => ({ ...prev, shipper_email: '' }));
                        }}
                        data-testid="switch-account-btn"
                      >
                        Not you? Switch account
                      </button>
                    </div>
                  )}
                </div>

                {/* Registration Form for New Users */}
                {showRegistration && accountStatus === 'new' && (
                  <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                    <h3 className="font-semibold text-blue-900">Create Your Shipper Account</h3>
                    <p className="text-sm text-blue-700">To post loads and receive quotes, please create an account.</p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="reg_name">Full Name *</Label>
                        <Input
                          id="reg_name"
                          name="name"
                          value={registrationData.name}
                          onChange={handleRegistrationChange}
                          required
                          placeholder="John Smith"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="reg_phone">Phone *</Label>
                        <Input
                          id="reg_phone"
                          name="phone"
                          type="tel"
                          value={registrationData.phone}
                          onChange={handleRegistrationChange}
                          placeholder="04XX XXX XXX"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="reg_password">Password *</Label>
                        <Input
                          id="reg_password"
                          name="password"
                          type="password"
                          value={registrationData.password}
                          onChange={handleRegistrationChange}
                          required
                          placeholder="Min 6 characters"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="reg_confirmPassword">Confirm Password *</Label>
                        <Input
                          id="reg_confirmPassword"
                          name="confirmPassword"
                          type="password"
                          value={registrationData.confirmPassword}
                          onChange={handleRegistrationChange}
                          required
                          placeholder="Confirm password"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="reg_company_name">Company Name</Label>
                        <Input
                          id="reg_company_name"
                          name="company_name"
                          value={registrationData.company_name}
                          onChange={handleRegistrationChange}
                          placeholder="Your Company Pty Ltd"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="reg_abn">ABN (Optional)</Label>
                        <Input
                          id="reg_abn"
                          name="abn"
                          value={registrationData.abn}
                          onChange={handleRegistrationChange}
                          placeholder="XX XXX XXX XXX"
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <Label htmlFor="reg_address">Business Address</Label>
                        <Input
                          id="reg_address"
                          name="address"
                          value={registrationData.address}
                          onChange={handleRegistrationChange}
                          placeholder="123 Main St, Sydney NSW 2000"
                        />
                      </div>
                    </div>
                    
                    <p className="text-xs text-blue-600">
                      Your account will be created when you submit your load. You'll be able to log in to track quotes and manage your loads.
                    </p>
                  </div>
                )}
                
                {/* Show contact fields only after account is verified or being created */}
                {(accountStatus === 'exists' || accountStatus === 'new') && (
                  <>
                    <div>
                      <Label htmlFor="contact_phone">Contact Phone *</Label>
                      <Input
                        id="contact_phone"
                        name="contact_phone"
                        type="tel"
                        value={formData.contact_phone}
                        onChange={handleChange}
                        required
                        placeholder="04XX XXX XXX"
                      />
                    </div>

                    <div>
                      <Label htmlFor="company_name">Company Name</Label>
                      <Input
                        id="company_name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder="Your Company Pty Ltd"
                      />
                    </div>

                    <div>
                      <Label htmlFor="additional_contact_person">Additional Contact Person</Label>
                      <Input
                        id="additional_contact_person"
                        name="additional_contact_person"
                        value={formData.additional_contact_person}
                        onChange={handleChange}
                        placeholder="Backup contact name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="additional_contact_phone">Additional Contact Phone</Label>
                      <Input
                        id="additional_contact_phone"
                        name="additional_contact_phone"
                        type="tel"
                        value={formData.additional_contact_phone}
                        onChange={handleChange}
                        placeholder="04XX XXX XXX"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Pickup & Delivery - Only show if account is verified */}
            {(accountStatus === 'exists' || accountStatus === 'new') && (
            <>
            {/* Pickup & Delivery */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">Pickup & Delivery Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pickup_suburb">Pickup Suburb *</Label>
                  <Input
                    id="pickup_suburb"
                    name="pickup_suburb"
                    value={formData.pickup_suburb}
                    onChange={handleChange}
                    required
                    placeholder="e.g. Parramatta"
                    data-testid="pickup-suburb-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pickup_state">Pickup State *</Label>
                  <select
                    id="pickup_state"
                    name="pickup_state"
                    value={formData.pickup_state}
                    onChange={handleChange}
                    required
                    className="w-full h-10 border rounded-md px-3 text-sm"
                    data-testid="pickup-state-select"
                  >
                    <option value="">Select State</option>
                    <option value="NSW">NSW</option>
                    <option value="VIC">VIC</option>
                    <option value="QLD">QLD</option>
                    <option value="WA">WA</option>
                    <option value="SA">SA</option>
                    <option value="TAS">TAS</option>
                    <option value="NT">NT</option>
                    <option value="ACT">ACT</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="dropoff_suburb">Dropoff Suburb *</Label>
                  <Input
                    id="dropoff_suburb"
                    name="dropoff_suburb"
                    value={formData.dropoff_suburb}
                    onChange={handleChange}
                    required
                    placeholder="e.g. Port Lincoln"
                    data-testid="dropoff-suburb-input"
                  />
                </div>
                <div>
                  <Label htmlFor="dropoff_state">Dropoff State *</Label>
                  <select
                    id="dropoff_state"
                    name="dropoff_state"
                    value={formData.dropoff_state}
                    onChange={handleChange}
                    required
                    className="w-full h-10 border rounded-md px-3 text-sm"
                    data-testid="dropoff-state-select"
                  >
                    <option value="">Select State</option>
                    <option value="NSW">NSW</option>
                    <option value="VIC">VIC</option>
                    <option value="QLD">QLD</option>
                    <option value="WA">WA</option>
                    <option value="SA">SA</option>
                    <option value="TAS">TAS</option>
                    <option value="NT">NT</option>
                    <option value="ACT">ACT</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="pickup_date">Available From</Label>
                  <Input
                    id="pickup_date"
                    name="pickup_date"
                    type="date"
                    value={formData.pickup_date}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Label htmlFor="delivery_date">Delivery Date</Label>
                  <Input
                    id="delivery_date"
                    name="delivery_date"
                    type="date"
                    value={formData.delivery_date}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Label htmlFor="pickup_time_window">Pickup Time Window</Label>
                  <Input
                    id="pickup_time_window"
                    name="pickup_time_window"
                    value={formData.pickup_time_window}
                    onChange={handleChange}
                    placeholder="e.g., 8AM - 12PM"
                  />
                </div>

                <div>
                  <Label htmlFor="delivery_time_window">Delivery Time Window</Label>
                  <Input
                    id="delivery_time_window"
                    name="delivery_time_window"
                    value={formData.delivery_time_window}
                    onChange={handleChange}
                    placeholder="e.g., 1PM - 5PM"
                  />
                </div>
              </div>
            </div>

            {/* Cargo Details */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">Cargo Information</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="cargo_type">Cargo Type *</Label>
                  <Select
                    value={formData.cargo_type}
                    onValueChange={(value) => handleSelectChange('cargo_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select cargo type" />
                    </SelectTrigger>
                    <SelectContent>
                      {mockCargoCategories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="cargo_description">Cargo Description</Label>
                  <Textarea
                    id="cargo_description"
                    name="cargo_description"
                    value={formData.cargo_description}
                    onChange={handleChange}
                    placeholder="Provide detailed description of your cargo..."
                    rows={3}
                  />
                </div>

                {/* Freight Image Upload */}
                <div>
                  <Label htmlFor="freight_image">Freight Photo (Optional)</Label>
                  <div className="mt-2">
                    <input
                      id="freight_image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-lg file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100
                        cursor-pointer"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Upload a photo of your freight (JPG, PNG, max 5MB)
                    </p>
                  </div>
                  
                  {imagePreview && (
                    <div className="mt-4 relative inline-block">
                      <img
                        src={imagePreview}
                        alt="Freight preview"
                        className="max-w-xs max-h-48 rounded border"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700"
                      >
                        ×
                      </button>
                      {freightImage && (
                        <p className="text-sm text-green-600 mt-2">
                          ✓ {freightImage.name} ({(freightImage.size / 1024).toFixed(1)} KB)
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="weight">Weight</Label>
                    <div className="flex gap-2">
                      <Input
                        id="weight"
                        name="weight"
                        type="number"
                        value={formData.weight}
                        onChange={handleChange}
                        placeholder="1000"
                        className="flex-1"
                      />
                      <select
                        id="weight_unit"
                        name="weight_unit"
                        value={formData.weight_unit}
                        onChange={handleChange}
                        className="w-20 h-10 px-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500"
                        data-testid="weight-unit-select"
                      >
                        <option value="kg">kg</option>
                        <option value="t">t</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label>Dimensions</Label>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      <div>
                        <Input
                          name="dimension_length"
                          value={formData.dimension_length}
                          onChange={handleChange}
                          placeholder="Length"
                          data-testid="dimension-length"
                        />
                        <p className="text-xs text-gray-400 mt-0.5 text-center">Length</p>
                      </div>
                      <div>
                        <Input
                          name="dimension_width"
                          value={formData.dimension_width}
                          onChange={handleChange}
                          placeholder="Width"
                          data-testid="dimension-width"
                        />
                        <p className="text-xs text-gray-400 mt-0.5 text-center">Width</p>
                      </div>
                      <div>
                        <Input
                          name="dimension_height"
                          value={formData.dimension_height}
                          onChange={handleChange}
                          placeholder="Height"
                          data-testid="dimension-height"
                        />
                        <p className="text-xs text-gray-400 mt-0.5 text-center">Height</p>
                      </div>
                    </div>
                    <div className="flex justify-end mt-1">
                      <select
                        name="dimension_unit"
                        value={formData.dimension_unit}
                        onChange={handleChange}
                        className="w-20 h-8 px-2 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500"
                        data-testid="dimension-unit-select"
                      >
                        <option value="m">m</option>
                        <option value="mm">mm</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="cargo_value">Cargo Value ($)</Label>
                    <Input
                      id="cargo_value"
                      name="cargo_value"
                      type="number"
                      value={formData.cargo_value}
                      onChange={handleChange}
                      placeholder="10000"
                    />
                  </div>
                </div>

                {/* Oversize Load Additional Fields */}
                {formData.cargo_type === 'Oversize Load' && (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h3 className="text-sm font-semibold text-yellow-900 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Oversize Load - Additional Dimension Details Required
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="oversize_ground_contact_dimensions" className="text-sm font-medium">
                          Ground Contact Width (Deck/Tyre/Base) *
                        </Label>
                        <Input
                          id="oversize_ground_contact_dimensions"
                          name="oversize_ground_contact_dimensions"
                          value={formData.oversize_ground_contact_dimensions}
                          onChange={handleChange}
                          placeholder="e.g., 3.5m wide, 12m long"
                          className="mt-1"
                          required={formData.cargo_type === 'Oversize Load'}
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          Specify the dimensions where the load contacts the ground (deck width, tyre width, base dimensions)
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="oversize_extremity_dimensions" className="text-sm font-medium">
                          Outermost Extremity Dimensions (LxWxH) *
                        </Label>
                        <Input
                          id="oversize_extremity_dimensions"
                          name="oversize_extremity_dimensions"
                          value={formData.oversize_extremity_dimensions}
                          onChange={handleChange}
                          placeholder="e.g., 15m x 4.5m x 4.2m"
                          className="mt-1"
                          required={formData.cargo_type === 'Oversize Load'}
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          Specify the maximum overall dimensions including any overhangs or protruding parts
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {/* Palletised Freight - Per-Pallet Breakdown */}
                {formData.cargo_type === 'Palletised Freight' && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="pallet-section">
                    <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      Palletised Freight — Per-Pallet Details
                    </h3>
                    <p className="text-xs text-gray-700 mb-3">
                      Enter the number of pallets, then provide weight and dimensions for each one.
                      Carriers need this to plan loading layout.
                    </p>

                    <div className="mb-4 max-w-xs">
                      <Label htmlFor="pallet_count" className="text-sm font-medium">
                        How many pallets? <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="pallet_count"
                        type="number"
                        min="1"
                        max="50"
                        value={formData.pallets.length || ''}
                        onChange={handlePalletCountChange}
                        placeholder="e.g. 4"
                        className="mt-1"
                        data-testid="pallet-count-input"
                      />
                    </div>

                    {formData.pallets.length > 0 && (
                      <div className="space-y-3">
                        {formData.pallets.map((p, idx) => (
                          <div
                            key={idx}
                            className="p-3 bg-white border border-blue-200 rounded-md"
                            data-testid={`pallet-row-${idx}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-blue-900">
                                Pallet {idx + 1}
                              </span>
                            </div>
                            <div className="grid sm:grid-cols-5 gap-2">
                              {/* Weight + unit */}
                              <div className="sm:col-span-2">
                                <Label className="text-xs">Weight *</Label>
                                <div className="flex gap-1">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={p.weight}
                                    onChange={(e) => handlePalletFieldChange(idx, 'weight', e.target.value)}
                                    placeholder="500"
                                    className="flex-1"
                                    data-testid={`pallet-weight-${idx}`}
                                  />
                                  <select
                                    value={p.weight_unit}
                                    onChange={(e) => handlePalletFieldChange(idx, 'weight_unit', e.target.value)}
                                    className="w-16 h-10 px-1 text-sm rounded-md border border-gray-300"
                                    data-testid={`pallet-weight-unit-${idx}`}
                                  >
                                    <option value="kg">kg</option>
                                    <option value="t">t</option>
                                  </select>
                                </div>
                              </div>
                              {/* L W H */}
                              <div>
                                <Label className="text-xs">Length *</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={p.length}
                                  onChange={(e) => handlePalletFieldChange(idx, 'length', e.target.value)}
                                  placeholder="1.2"
                                  data-testid={`pallet-length-${idx}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Width *</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={p.width}
                                  onChange={(e) => handlePalletFieldChange(idx, 'width', e.target.value)}
                                  placeholder="1.0"
                                  data-testid={`pallet-width-${idx}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Height *</Label>
                                <div className="flex gap-1">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={p.height}
                                    onChange={(e) => handlePalletFieldChange(idx, 'height', e.target.value)}
                                    placeholder="1.5"
                                    className="flex-1"
                                    data-testid={`pallet-height-${idx}`}
                                  />
                                  <select
                                    value={p.dimension_unit}
                                    onChange={(e) => handlePalletFieldChange(idx, 'dimension_unit', e.target.value)}
                                    className="w-16 h-10 px-1 text-sm rounded-md border border-gray-300"
                                    data-testid={`pallet-dim-unit-${idx}`}
                                  >
                                    <option value="m">m</option>
                                    <option value="mm">mm</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {formData.pallets.length === 0 && (
                      <p className="text-xs text-gray-500 italic">
                        Enter a number above to add pallet rows.
                      </p>
                    )}

                    <p className="text-xs text-blue-700 mt-3">
                      Total weight and a per-pallet summary will be auto-calculated when you submit.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">Requirements</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="insurance_requirements">Insurance Requirements</Label>
                  <Textarea
                    id="insurance_requirements"
                    name="insurance_requirements"
                    value={formData.insurance_requirements}
                    onChange={handleChange}
                    placeholder="Specify insurance coverage requirements..."
                    rows={2}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="loading_equipment_needed">Loading Equipment Needed</Label>
                    <select
                      id="loading_equipment_needed"
                      name="loading_equipment_needed"
                      value={formData.loading_equipment_needed}
                      onChange={handleChange}
                      className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500"
                      data-testid="loading-equipment-select"
                    >
                      <option value="">Select loading equipment...</option>
                      <option value="Forklift on site">Forklift on site</option>
                      <option value="Crane required">Crane required</option>
                      <option value="Side loader required">Side loader required</option>
                      <option value="Loading ramp available">Loading ramp available</option>
                      <option value="Roll on/Roll off">Roll on/Roll off</option>
                      <option value="Please supply loading equipment">Please supply loading equipment</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="unloading_equipment_needed">Unloading Equipment Needed</Label>
                    <select
                      id="unloading_equipment_needed"
                      name="unloading_equipment_needed"
                      value={formData.unloading_equipment_needed}
                      onChange={handleChange}
                      className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500"
                      data-testid="unloading-equipment-select"
                    >
                      <option value="">Select unloading equipment...</option>
                      <option value="Forklift on site">Forklift on site</option>
                      <option value="Crane required">Crane required</option>
                      <option value="Side loader required">Side loader required</option>
                      <option value="Unloading ramp available">Unloading ramp available</option>
                      <option value="Roll on/Roll off">Roll on/Roll off</option>
                      <option value="Please supply unloading equipment">Please supply unloading equipment</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="special_requirements">Special Requirements</Label>
                  <Textarea
                    id="special_requirements"
                    name="special_requirements"
                    value={formData.special_requirements}
                    onChange={handleChange}
                    placeholder="Any special handling requirements..."
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={loading || (accountStatus === null)}
                className="bg-red-600 hover:bg-red-700 text-white flex-1"
              >
                {loading ? 'Submitting...' : accountStatus === 'new' ? 'Create Account & Post Load' : 'Submit Request'}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/')}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
            </>
            )}

            {/* Message to verify email if not done yet */}
            {accountStatus === null && formData.shipper_email && (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-gray-600">Please click "Verify Email" above to continue</p>
              </div>
            )}
          </form>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default RequestQuote;
